import type { SpaceVersion } from '@lighthouse/core'
import type { FC, PropsWithChildren } from 'react'
import React, { createContext, useContext } from 'react'

import type { OpenWechatDocument } from '../components/FilePreviewer/types'
import type { DeptOption, RoleOption, UserOption } from '../types'

export interface VideoPlayUrlResponse {
    playUrl: string
    status: 'READY' | 'PROCESSING' | 'FAILED'
    expireAt: number
    message: string
}

export interface VideoCoverUrlResponse {
    fileId: string
    exists: boolean
    coverUrl: string | null
    expireAt: number | null
    message: string
}

export type ApplicationContextType = {
    appId: string | undefined
    personOptions: UserOption[]
    roleOptions: RoleOption[]
    departmentOptions: DeptOption[]
    pageTarget?: string
    /**
     * 是否使用移动端 UI 样式
     * - 管理端：跟随当前选中断点的 type
     * - 应用端：基于 UA 检测
     */
    isMobile: boolean
    isBuilder: boolean
    /** 空间版本（用于运行时权益控制） */
    spaceVersion?: SpaceVersion
    /** 获取阿里云播放凭证 */
    fetchVideoPlayAuth?: (videoId: string) => Promise<string>
    /** 获取视频首帧封面 */
    fetchVideoCoverUrl?: (fileId: string) => Promise<VideoCoverUrlResponse | null>
    miniProgramId?: string
    /** 签发视频播放地址（含 HLS 鉴权） */
    fetchAVPlayUrl?: (originalUrl: string, isOrigin?: boolean) => Promise<VideoPlayUrlResponse>

    /** 是否有更新 */
    hasUpdate?: boolean
    mutateHasUpdate?: (v?: boolean) => void
    /** 向小程序发送消息 */
    sendMessageToMiniProgram?: (params: {
        messageType: 'MINI_PROGRAM_DOWNLOAD_FILE'
        wsId: string
        fileUrl: string
        fileName: string
    }) => Promise<boolean>
    /** 在小程序中打开微信支持的文档 */
    openWechatDocument?: OpenWechatDocument
}

export const ApplicationContext = createContext<ApplicationContextType>({
    appId: '',
    personOptions: [],
    roleOptions: [],
    departmentOptions: [],
    isMobile: false,
    isBuilder: false,
    hasUpdate: false,
    mutateHasUpdate: () => void 0
})

type ApplicationProviderProps = PropsWithChildren<{
    value: ApplicationContextType
}>

export const ApplicationProvider: FC<ApplicationProviderProps> = props => <ApplicationContext.Provider {...props} />

export const useApplicationContext = () => useContext(ApplicationContext)
